import { useState, useRef, useEffect } from 'react';
import { PDFDocument, rgb } from 'pdf-lib';
import { Download, Square, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Badge } from '@/components/ui/Badge';
import { useApp } from '@/store/app';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';
import { pdfjs } from '@/lib/pdfjs';

interface Box {
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export function PdfRedact() {
  const { pendingFile, setPendingFile } = useApp();
  const [file, setFile] = useState<File | null>(null);
  const [pages, setPages] = useState<{ width: number; height: number; img: string }[]>([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [draft, setDraft] = useState<{ start: { x: number; y: number }; end: { x: number; y: number } } | null>(null);
  const [working, setWorking] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (pendingFile && pendingFile.type === 'application/pdf') {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setFile(f);
    setBoxes([]);
    setPageIdx(0);
    const buf = await readFileAsArrayBuffer(f);
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    const list: typeof pages = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const viewport = page.getViewport({ scale: 1.5 });
      const c = document.createElement('canvas');
      c.width = viewport.width;
      c.height = viewport.height;
      const ctx = c.getContext('2d')!;
      await page.render({ canvasContext: ctx, viewport, canvas: c }).promise;
      list.push({
        width: viewport.width,
        height: viewport.height,
        img: c.toDataURL('image/png'),
      });
    }
    setPages(list);
  }

  function pos(e: React.PointerEvent<HTMLDivElement>) {
    const rect = ref.current!.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }

  function down(e: React.PointerEvent<HTMLDivElement>) {
    const p = pos(e);
    setDraft({ start: p, end: p });
  }
  function move(e: React.PointerEvent<HTMLDivElement>) {
    if (!draft) return;
    setDraft({ start: draft.start, end: pos(e) });
  }
  function up() {
    if (!draft) return;
    const x = Math.min(draft.start.x, draft.end.x);
    const y = Math.min(draft.start.y, draft.end.y);
    const w = Math.abs(draft.start.x - draft.end.x);
    const h = Math.abs(draft.start.y - draft.end.y);
    if (w > 0.005 && h > 0.005) {
      setBoxes((arr) => [...arr, { page: pageIdx, x, y, w, h }]);
    }
    setDraft(null);
  }

  function clearPage() {
    setBoxes((arr) => arr.filter((b) => b.page !== pageIdx));
  }

  async function exportRedacted() {
    if (!file) return;
    setWorking(true);
    try {
      const buf = await readFileAsArrayBuffer(file);
      const out = await PDFDocument.load(buf);
      const docPages = out.getPages();
      for (const b of boxes) {
        const p = docPages[b.page];
        const { width, height } = p.getSize();
        // y-axis flip: pdf-lib origin is bottom-left.
        p.drawRectangle({
          x: b.x * width,
          y: height - (b.y + b.h) * height,
          width: b.w * width,
          height: b.h * height,
          color: rgb(0, 0, 0),
        });
      }
      const bytes = await out.save();
      saveAs(bytesToBlob(bytes), file.name.replace(/\.pdf$/i, '') + '-redacted.pdf');
      toast.success('Redacted PDF saved');
    } catch {
      toast.error('Redaction failed');
    } finally {
      setWorking(false);
    }
  }

  const currentPage = pages[pageIdx];
  const pageBoxes = boxes.filter((b) => b.page === pageIdx);

  return (
    <div>
      <ToolHeader
        toolId="pdf-redact"
        title="Redact PDF"
        description="Drag to draw black rectangles over sensitive content. Saves redactions into the PDF (fully covered, not just hidden)."
        badges={boxes.length ? <Badge>{boxes.length} redactions</Badge> : null}
      />

      {!file ? (
        <Dropzone accept="application/pdf" onFiles={handleFile} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:order-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium truncate">{file.name}</span>
              <Button size="sm" variant="ghost" onClick={() => { setFile(null); setPages([]); setBoxes([]); }}>
                Change
              </Button>
            </div>
            <div className="flex items-center justify-between text-sm">
              <Button
                size="sm"
                variant="ghost"
                disabled={pageIdx === 0}
                onClick={() => setPageIdx((i) => Math.max(0, i - 1))}
              >
                <ChevronLeft size={12} />
              </Button>
              <span className="font-mono text-xs">
                {pageIdx + 1} / {pages.length}
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={pageIdx >= pages.length - 1}
                onClick={() => setPageIdx((i) => Math.min(pages.length - 1, i + 1))}
              >
                <ChevronRight size={12} />
              </Button>
            </div>
            <div className="text-xs text-fg-muted">
              Click and drag on the page to draw a black rectangle.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="secondary" onClick={clearPage} disabled={!pageBoxes.length}>
                <Trash2 size={11} />
                Clear page
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setBoxes([])} disabled={!boxes.length}>
                Clear all
              </Button>
            </div>
            <Button
              variant="primary"
              className="w-full"
              loading={working}
              disabled={!boxes.length || working}
              onClick={exportRedacted}
            >
              <Download size={11} />
              Export redacted
            </Button>
          </Card>

          <Card className="lg:col-span-2 lg:order-1 !p-3 overflow-hidden">
            {currentPage && (
              <div
                ref={ref}
                onPointerDown={down}
                onPointerMove={move}
                onPointerUp={up}
                onPointerLeave={up}
                className="relative inline-block max-w-full mx-auto cursor-crosshair shadow-card rounded touch-none select-none"
                style={{ width: '100%', maxWidth: currentPage.width }}
              >
                <img
                  src={currentPage.img}
                  alt={`page ${pageIdx + 1}`}
                  className="w-full h-auto block pointer-events-none"
                />
                {pageBoxes.map((b, i) => (
                  <div
                    key={i}
                    className="absolute bg-black"
                    style={{
                      left: `${b.x * 100}%`,
                      top: `${b.y * 100}%`,
                      width: `${b.w * 100}%`,
                      height: `${b.h * 100}%`,
                    }}
                  />
                ))}
                {draft && (
                  <div
                    className="absolute border-2 border-accent bg-accent/30 pointer-events-none"
                    style={{
                      left: `${Math.min(draft.start.x, draft.end.x) * 100}%`,
                      top: `${Math.min(draft.start.y, draft.end.y) * 100}%`,
                      width: `${Math.abs(draft.start.x - draft.end.x) * 100}%`,
                      height: `${Math.abs(draft.start.y - draft.end.y) * 100}%`,
                    }}
                  />
                )}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

export default PdfRedact;
